import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ShiftSchema, ShiftCreateInputSchema, ShiftUpdateInputSchema, type Shift, type ShiftCreateInput, type ShiftUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `shift` (créneau de travail planifié, org-wide).
 *
 * Réplique le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`ShiftCreateInputSchema`) AVANT toute écriture Firestore. Les liens vers
 * d'autres familles (worker, place, route, absence, availability) sont de
 * simples ids ; `workerId` (l'intervenant affecté) est le cœur de la famille.
 */

export type { ShiftCreateInput };

/** Nom de la collection Firestore de la famille shift. */
export const SHIFTS_COLLECTION = 'shifts';

/**
 * Repository typé pour la famille `shift`.
 * Toute lecture est validée par `ShiftSchema`, toute écriture par
 * `ShiftCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface ShiftRepository {
  create(input: ShiftCreateInput): Promise<Shift>;
  update(id: string, patch: ShiftUpdateInput): Promise<Shift>;
  list(options?: ListOptions): Promise<Shift[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Shift | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Shift>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Shift>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Shift>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const shiftConverter = createConverter<Shift>((raw: DocumentData) => ShiftSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createShiftRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ShiftRepository {
  const col = collection(db, SHIFTS_COLLECTION).withConverter(shiftConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Shift>(db, col, SHIFTS_COLLECTION, getActorId);

  return {
    async create(input: ShiftCreateInput): Promise<Shift> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = ShiftCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.endAt !== undefined) optional['endAt'] = fields.endAt;
      if (fields.roleLabel !== undefined) optional['roleLabel'] = fields.roleLabel;
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.routeId !== undefined) optional['routeId'] = fields.routeId;
      if (fields.absenceId !== undefined) optional['absenceId'] = fields.absenceId;
      if (fields.availabilityId !== undefined) optional['availabilityId'] = fields.availabilityId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        workerId: fields.workerId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Shift);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (startAt/endAt sont déjà des
      // Date issues de l'entrée validée).
      return ShiftSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: ShiftUpdateInput): Promise<Shift> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = ShiftUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, SHIFTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`shift ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Shift[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Shift | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
